import React, { useEffect, useState, useRef } from "react"; // Import useRef
import { Dialog, Box, Typography, Chip } from '@mui/material';
import MuiSelectDropdown from 'components/mui3-forms/MuiSelectDropdown';
import MuiMultiSelectList from 'components/mui-forms/MuiMultiSelectList';
import * as taskTemplateStyle from '../../kart/fieldmap/tasktemplates/TaskTemplateStyle';

const NewAddTaskModalWindow = (props) => {
  const {
    intl,
    onCreateTask,
    fieldDetails,
    userActivities,
    closeNewTask,
    handleSave
  } = props;

  const [selectedActivityId, setSelectedActivityId] = useState('');
  const [selectedFieldId, setSelectedFieldId] = useState([]);
  const [selectedActivityName, setSelectedActivityName] = useState('');
  const [selectedFieldName, setSelectedFieldName] = useState([]);
  const [isModalOpen, setModalOpen] = useState(false);

  const initialStateRef = useRef({
    activityId: '',
    fieldId: [],
  });

  useEffect(() => {
    if (onCreateTask) {
      initialStateRef.current = {
        activityId: selectedActivityId,
        fieldId: selectedFieldId,
      };
    }
  }, [onCreateTask]);

  const uniqueActivities = new Map();

  for (const category in userActivities.userActivitiesList) {
    userActivities.userActivitiesList[category].activities.forEach(activity => {
      if (activity.active) {
        if (!uniqueActivities.has(activity.id)) {
          uniqueActivities.set(activity.id, {
            activityName: activity.activityName,
            activityId: activity.id
          });
        }
      }
    });
  }

  const activityOptions = Array.from(uniqueActivities.values()).map(activity => ({
    label: activity.activityName,
    value: activity.activityId,
  }));

  const fieldListOptions = fieldDetails.data1?.fields?.map(field => ({
    label: field.fieldName,
    value: field.fieldId,
  })) || [];

  const handleTypeChange = (name, value) => {
    if (name === 'activity') {
      setSelectedActivityId(value);
    } else if (name === 'field') {
      setSelectedFieldId(value || []);
    }
  };

  useEffect(() => {
    if (selectedActivityId && activityOptions) {
      const activity = activityOptions.find(
        (option) => option.value === selectedActivityId
      );
      setSelectedActivityName(activity ? activity.label : '');
    } else {
      setSelectedActivityName('');
    }
  }, [selectedActivityId, activityOptions]);

  useEffect(() => {
    if (selectedFieldId && selectedFieldId.length > 0 && fieldDetails.data1?.fields) {
      const selectedNames = selectedFieldId.map(id => {
        const field = fieldDetails.data1.fields.find(f => f.fieldId === id);
        return field ? field.fieldName : '';
      }).filter(Boolean);
      setSelectedFieldName(selectedNames);
    } else {
      setSelectedFieldName([]);
    }
  }, [selectedFieldId, fieldDetails]);

  const handleCancelButton = () => {
    setModalOpen(false); 
    closeNewTask(); 
    // Reset selected states to initial or empty values
    setSelectedActivityId('');
    setSelectedFieldId([]);
    setSelectedActivityName('');
    setSelectedFieldName([]);
  };

  const handleCancel = () => {
    // Check if activity or field selections have changed from their initial state
    const currentFormState = {
      activityId: selectedActivityId,
      fieldId: selectedFieldId,
    };

    const isChanged =
      JSON.stringify(initialStateRef.current.activityId) !== JSON.stringify(currentFormState.activityId) ||
      JSON.stringify(initialStateRef.current.fieldId) !== JSON.stringify(currentFormState.fieldId);

    if (isChanged) {
      setModalOpen(true); // Open the confirmation modal
    } else {
      handleCancelButton(); // Directly close if no changes
    }
  };

  const handleSaveClick = () => {
    handleSave(selectedActivityId, selectedFieldId, selectedActivityName, selectedFieldName);
    setSelectedActivityId('');
    setSelectedFieldId([]);
    setSelectedActivityName('');
    setSelectedFieldName([]);
  };

  const handleContinueSave = () => {
    setModalOpen(false); 
  };

  const isActivityDropdownDisabled = activityOptions.length === 0;
  const isFieldDropdownDisabled = fieldListOptions.length === 0;

  // Logic to determine if save button should be enabled
  const isSaveButtonEnabled = selectedActivityId !== '' && selectedFieldId.length > 0;

  return (
    <Box>
      <Dialog
        onClose={() => { }}
        open={onCreateTask}
        PaperProps={{
          style: {
            backgroundColor: '#E6E9E8',
            borderRadius: "28px",
            width: "406px",
            maxWidth: "90vw",
            minWidth: "280px",
            maxHeight: '70vh',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{
          padding: '24px 24px 0px 24px',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#A8A8A8',
            borderRadius: '4px',
            transition: 'background-color 0.3s ease',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: '#B8B8B8',
          }
        }}>
          <Typography style={{ ...taskTemplateStyle.modalDialogTitle, paddingBottom: '48px' }}>
            {intl.formatMessage({ id: "NEW_REGISTRATION" })}
          </Typography>
          <Box>
            <MuiSelectDropdown
              defaultLabel={intl.formatMessage({ id: 'ACTIVITY_REGISTERATION' })}
              options={activityOptions}
              showEmptySpace={false}
              onChange={handleTypeChange}
              value={selectedActivityId}
              fieldName="activity"
              // disabled={isActivityDropdownDisabled}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '200px',
                  },
                },
              }}
            />
          </Box>
          <Box paddingTop='24px'>
            <MuiMultiSelectList
              label={intl.formatMessage({ id: 'FIELD_SELECTION' })}
              options={fieldListOptions}
              fullWidth={true}
              onChange={handleTypeChange}
              value={selectedFieldId}
              fieldName="field"
              // disabled={isFieldDropdownDisabled}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: '200px',
                  },
                },
              }}
            />
          </Box>
        </Box>

        <Box style={{ ...taskTemplateStyle.buttonBoxBottom, justifyContent: 'flex-end', paddingRight: '24px' }}>
          <Chip
            label={intl.formatMessage({ id: 'cancel' })}
            clickable
            onClick={handleCancel}
            sx={taskTemplateStyle.cancelButtonStyle}
            disableRipple
          />

          <Chip
            label={intl.formatMessage({ id: 'SUB_BALES_SAVE_BUTTON' })}
            clickable
            onClick={handleSaveClick}
            sx={{
              fontFamily: 'Inter',
              fontWeight: 500,
              height: '40px',
              borderRadius: '100px',
              padding: '0px 16px',
              fontSize: 14,
              color: isSaveButtonEnabled ? '#ffffff' : '#111111',
              backgroundColor: isSaveButtonEnabled ? '#00696D' : '#999999',
              '&:hover, &:focus': {
                backgroundColor: isSaveButtonEnabled ? '#005457' : '#999999',
              },
            }}
            disabled={!isSaveButtonEnabled}
            disableRipple
          />
        </Box>
      </Dialog>
      <Box>
        {/* Confirmation Dialog for unsaved changes */}
        <Box>
          <Dialog
            onClose={() => { }}
            open={isModalOpen}
            PaperProps={{
              style: {
                backgroundColor: '#E6E9E8',
                borderRadius: "28px",
                width: "312px",
                minWidth: "280px",
                maxWidth: "312px",
                maxHeight: '30vh'
              }
            }}
          >
            <Box style={taskTemplateStyle.cancelModalBox}>
              <Typography style={taskTemplateStyle.cancelModalTitle}>
                {/* Dynamically set title if you want, similar to the original snippet */}
                {intl.formatMessage({ id: 'TASK_CANCEL_MODAL_TITLE' })}
              </Typography>
              <Typography style={taskTemplateStyle.cancelModalSubTitle}>
                {/* Dynamically set subtitle */}
                {intl.formatMessage({ id: 'TASK_CANCEL_MODAL_SUB_TITLE' })}
              </Typography>
            </Box>
            <Box style={taskTemplateStyle.cancelModalBoxButton}>
              <Chip
                label={intl.formatMessage({ id: 'cancel' })}
                clickable
                style={taskTemplateStyle.cancelButtonStyle}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover, &:focus': {
                    backgroundColor: 'transparent',
                  }
                }}
                disableRipple
                onClick={handleCancelButton} 
              />
              <Chip
                label={intl.formatMessage({
                  id: 'TASK_CONTINUE_REGISTRATION'
                })}
                clickable
                style={taskTemplateStyle.cancelButtonStyle}
                sx={{
                  backgroundColor: 'transparent',
                  '&:hover, &:focus': {
                    backgroundColor: 'transparent',
                  }
                }}
                disableRipple
                onClick={handleContinueSave} 
              />
            </Box>
          </Dialog>
        </Box>
      </Box>
    </Box>
  );
};

export default NewAddTaskModalWindow;